import React, { useEffect, useState } from "react";
import SliderHeader from "../../../components/ui/sliderHeader/SliderHeader";
import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import IOSSwitch from "../../../components/ui/switch/IosSwitch";
import ClearIcon from "@mui/icons-material/Clear";
import "./styles/slider.css";

const SliderOption = ({ allData }) => {
  const [from, setFrom] = useState(allData.data[allData.index].from);
  const [to, setTo] = useState(allData.data[allData.index].to);
  const [isRangeValid, setIsRangeValid] = useState(true);
  const [rangeErrorMessage, setRangeErrorMessage] = useState("");
  const [optionalCountStep, setOptionalCountStep] = useState(
    allData.data[allData.index].includeCountStep
  );

  const handleChangeCheckedStep = (e) => {
    const checked = e.target.checked;
    setOptionalCountStep(checked ? true : false);
    allData.data[allData.index].includeCountStep = checked;
  };

  const validationRange = () => {
    if (from === "" || to === "") {
      setIsRangeValid(false);
      setRangeErrorMessage("This field cannot be empty");
    } else if (isNaN(from) || isNaN(to)) {
      setIsRangeValid(false);
      setRangeErrorMessage("Both 'From' and 'To' values are required");
    } else if (from === to) {
      setIsRangeValid(false);
      setRangeErrorMessage("Min and max value cannot be the same");
    } else if (from > to) {
      setIsRangeValid(false);
      setRangeErrorMessage("Max value cannot be lower than the min value");
    } else {
      setIsRangeValid(true);
      setRangeErrorMessage(""); // Clear the error message if the range is valid
    }
  };

  const handleRangeChange = (key, value) => {
    const numericValue = parseInt(value);

    if (value === "" || (numericValue >= 0 && numericValue <= 100000000)) {
      if (key === "from") {
        setFrom(value === "" ? "" : numericValue);
        allData.data[allData.index].from = value === "" ? "" : numericValue;
      } else if (key === "to") {
        setTo(value === "" ? "" : numericValue);
        allData.data[allData.index].to = value === "" ? "" : numericValue;
      }
    }
  };

  useEffect(() => {
    validationRange();
  }, [from, to]);

  useEffect(() => {
    if (rangeErrorMessage !== "") {
      const newEmptyTextErrors = {
        id: allData.data[allData.index].data.id,
        title:
          allData.data[allData.index].data.label === ""
            ? "Untitled"
            : `${allData.data[allData.index].data.label}`,
        subject: `${rangeErrorMessage}`,
      };

      allData.settingErrors((prevErrors) => {
        const updatedErrors = prevErrors.filter(
          (error) =>
            !(
              error.id === allData.data[allData.index].data.id &&
              (error.subject ===
                "Max value cannot be lower than the min value" ||
                error.subject === "Min and max value cannot be the same" ||
                error.subject === "Both 'From' and 'To' values are required" ||
                error.subject === "This field cannot be empty")
            )
        );

        return [...updatedErrors, newEmptyTextErrors];
      });
    } else {
      // Remove the error if emptyTimeText is false
      allData.settingErrors((prevErrors) =>
        prevErrors.filter(
          (error) =>
            !(
              error.id === allData.data[allData.index].data.id &&
              (error.subject ===
                "Max value cannot be lower than the min value" ||
                error.subject === "Min and max value cannot be the same" ||
                error.subject === "Both 'From' and 'To' values are required" ||
                error.subject === "This field cannot be empty")
            )
        )
      );
    }
  }, [rangeErrorMessage, allData.data[allData.index].data.label]);

  const [selectedCurrency, setSelectedCurrency] = useState(
    allData.data[allData.index].unitValue
  );

  // Step 3: Event handler function to update the state
  const handleCurrencyChange = (event) => {
    const unitValue = event.target.value;
    if (!isTextFieldDisabled()) {
      setSelectedCurrency(unitValue);
      allData.data[allData.index].unitValue = unitValue;
    }
  };

  const clearSelectedCurrency = () => {
    setSelectedCurrency("");
    allData.data[allData.index].unitValue = ""; // Clear the selected unit
  };

  const isTextFieldDisabled = () => {
    return !!selectedCurrency; // Returns true if a unit is selected, false otherwise
  };

  const currencies = [
    {
      value: "$",
      label: "$",
    },
    {
      value: "€",
      label: "€",
    },
    {
      value: "₹",
      label: "₹",
    },
    {
      value: "£",
      label: "£",
    },
    {
      value: "°C",
      label: "°C",
    },
    {
      value: "°F",
      label: "°F",
    },
  ];

  const [text, setText] = useState(
    allData.data[allData.index].data.description
  );

  const [isNameValid, setIsNameValid] = useState(false);

  const [emptySliderText, setEmptySliderText] = useState(false);

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (newValue.trim() === "") {
      setEmptySliderText(true);
    } else {
      setEmptySliderText(false);
    }
    setText(newValue);
    allData.data[allData.index].data.description = newValue;
    allData.reRender();
  };

  useEffect(() => {
    if (emptySliderText) {
      const newEmptyTextErrors = {
        id: allData.data[allData.index].data.id,
        title:
          allData.data[allData.index].data.label === ""
            ? "Untitled"
            : `${allData.data[allData.index].data.label}`,
        subject: "There is no text added in this action block",
      };

      allData.settingErrors((prevErrors) => {
        const updatedErrors = prevErrors.filter(
          (error) =>
            !(
              error.id === allData.data[allData.index].data.id &&
              error.subject === "There is no text added in this action block"
            )
        );

        return [...updatedErrors, newEmptyTextErrors];
      });
    } else {
      // Remove the error if emptySliderText is false
      allData.settingErrors((prevErrors) =>
        prevErrors.filter(
          (error) =>
            !(
              error.id === allData.data[allData.index].data.id &&
              error.subject === "There is no text added in this action block"
            )
        )
      );
    }
  }, [emptySliderText, allData.data[allData.index].data.label]);

  useEffect(() => {
    const isValid = text.trim() !== "" && /[a-zA-Z]/.test(text);

    setIsNameValid(isValid);
  }, [text]);

  const [selectedCountStep, setSelectedCountStep] = useState(
    allData.data[allData.index].countStep
  );

  const [isCountStepValid, setIsCountStepValid] = useState(true);
  const [countStepErrorMessage, setCountStepErrorMessage] = useState("");

  const validationCountStep = () => {
    if (selectedCountStep < 2 || selectedCountStep > 20) {
      setIsCountStepValid(false);
      setCountStepErrorMessage("Count step must be between 2 and 20");
    } else if (isNaN(selectedCountStep)) {
      setIsCountStepValid(false);
      setCountStepErrorMessage("This field cannot be empty");
    } else {
      setIsCountStepValid(true);
      setCountStepErrorMessage(""); // Clear the error message if the range is valid
    }
  };

  const handleCountStepChange = (e) => {
    const value = parseInt(e.target.value);
    setSelectedCountStep(value);
    allData.data[allData.index].countStep = value;
  };

  useEffect(() => {
    validationCountStep();
  }, [selectedCountStep]);

  useEffect(() => {
    if (optionalCountStep === false) {
      setSelectedCountStep(2);
      // Remove the error if optionalCountStep is true
      allData.settingErrors((prevErrors) =>
        prevErrors.filter(
          (error) =>
            !(
              error.id === allData.data[allData.index].data.id &&
              error.subject === "Step count value needs to be between 2 and 20"
            )
        )
      );
    } else {
      if (countStepErrorMessage !== "") {
        const newEmptyTextErrors = {
          id: allData.data[allData.index].data.id,
          title:
            allData.data[allData.index].data.label === ""
              ? "Untitled"
              : `${allData.data[allData.index].data.label}`,
          subject: "Step count value needs to be between 2 and 20",
        };

        allData.settingErrors((prevErrors) => {
          const updatedErrors = prevErrors.filter(
            (error) =>
              !(
                error.id === allData.data[allData.index].data.id &&
                error.subject ===
                  "Step count value needs to be between 2 and 20"
              )
          );

          return [...updatedErrors, newEmptyTextErrors];
        });
      } else {
        // Remove the error if emptyTimeText is false
        allData.settingErrors((prevErrors) =>
          prevErrors.filter(
            (error) =>
              !(
                error.id === allData.data[allData.index].data.id &&
                error.subject ===
                  "Step count value needs to be between 2 and 20"
              )
          )
        );
      }
    }
  }, [
    countStepErrorMessage,
    allData.data[allData.index].data.label,
    optionalCountStep,
  ]);

  useEffect(() => {
    setFrom(allData.data[allData.index].from);
    setTo(allData.data[allData.index].to);
    setSelectedCountStep(allData.data[allData.index].countStep);
    setText(allData.data[allData.index].data.description);
    setOptionalCountStep(allData.data[allData.index].includeCountStep);
    setSelectedCurrency(allData.data[allData.index].unitValue);
  }, [allData.index, allData.data]);

  return (
    <Box className="slider" style={{ right: allData.isOpen ? "0" : "-350px" }}>
      <SliderHeader
        closeData={allData}
        headerMessage={
          "Slider lets the user select a number from a number range in an intuitive UI"
        }
        toastHeader="Slider"
        nodeType={allData.nodeType}
        isTextValid={!isNameValid || !isRangeValid}
      ></SliderHeader>

      <Box className="slider-comp">
        <Box className="email-subject">
          <Typography className="text-header">Ask this question</Typography>
          <TextField
            id="subject"
            multiline
            inputProps={{ maxLength: 75 }}
            value={text}
            onChange={handleChange}
          ></TextField>
          <Stack flexDirection="row" justifyContent="space-around">
            <Typography
              variant="body2"
              color={isNameValid ? "#333333" : "error"}
              className="slider-error-message"
            >
              {isNameValid ? "Valid" : "Not Valid"}
            </Typography>
            <Typography variant="body2" className="remaining-text">
              {`${75 - text.length}`} Remaining
            </Typography>
          </Stack>
        </Box>
        <Box className="range-slider">
          <Typography className="text-header">
            Enter the range of slider values
          </Typography>
          <Box className="range-cont">
            <TextField
              type="number"
              id="fromRange"
              value={from}
              onChange={(e) => handleRangeChange("from", e.target.value)}
              onKeyPress={(e) => {
                // Allow only digits, Backspace, and Enter
                const isValidInput = /^[0-9\b\r]+$/.test(e.key);
                if (!isValidInput) {
                  e.preventDefault();
                }
              }}
              className="from-text"
            />
            <TextField
              type="number"
              id="toRange"
              value={to}
              onChange={(e) => handleRangeChange("to", e.target.value)}
              onKeyPress={(e) => {
                // Allow only digits, Backspace, and Enter
                const isValidInput = /^[0-9\b\r]+$/.test(e.key);
                if (!isValidInput) {
                  e.preventDefault();
                }
              }}
              className="to-text"
            />
          </Box>
          {!isRangeValid && (
            <Typography className="error-range">{rangeErrorMessage}</Typography>
          )}
        </Box>
        <Box className="count-slider">
          <Box className="count-cont">
            <Box>
              <Typography className="enable-move">
                Enable movement using step count
              </Typography>
              <Typography className="des-enable-move">
                Enabling this will move the slider in increments of the step
                count.
              </Typography>
            </Box>
            <IOSSwitch
              checked={optionalCountStep === true}
              onChange={handleChangeCheckedStep}
            />
          </Box>
        </Box>
        {optionalCountStep && (
          <Box className="count-step-slider">
            <Box className="count-step-cont">
              <Box style={{ flex: "2" }}>
                <Typography className="count-p">
                  Count of steps to be displayed
                </Typography>
              </Box>
              <TextField
                id="outlined-count-step"
                type="number"
                value={selectedCountStep}
                onChange={handleCountStepChange}
                onKeyPress={(e) => {
                  // Allow only digits, Backspace, and Enter
                  const isValidInput = /^[0-9\b\r]+$/.test(e.key);
                  if (!isValidInput) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  min: 2,
                  max: 20,
                }}
                style={{ flex: "1" }}
              ></TextField>
            </Box>
            {isCountStepValid ? (
              <Typography className="des-count-step">
                The number of steps displayed in the slider.The step count is
                auto-generated using this value Between (2:20).
              </Typography>
            ) : (
              <Typography className="error-count-step">
                {countStepErrorMessage}
              </Typography>
            )}
          </Box>
        )}
        <Box className="unit-slider">
          <Box className="unit-cont">
            <Typography className="unit-p">Unit for slider values</Typography>
            <TextField
              id="outlined-select-currency"
              select
              style={{ flex: "1" }}
              value={selectedCurrency}
              onChange={handleCurrencyChange}
              InputProps={{
                endAdornment: selectedCurrency && (
                  <IconButton
                    style={{ marginRight: "20px" }}
                    onClick={clearSelectedCurrency}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
              disabled={isTextFieldDisabled()}
            >
              {currencies.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default SliderOption;
